import { useHoverState } from '@engoo/react-hover-state';
import SelectState from 'app/components/forms/SelectState';
import { BASE_HEIGHT, BASE_HEIGHT_PX, ICON_SIZE_SMALL_PX } from 'app/components/search/Input';
import ActionArrow from 'app/components/svgs/ActionArrow';
import { PAGE_BACKGROUND_COLOR } from 'app/constants/Colors';
import { SEARCH_INPUT_BOX_SHADOW, SEARCH_INPUT_FOCUS_BOX_SHADOW } from 'app/constants/Styles';
import { useCss, IStyles } from 'app/utils/css';
import { List } from 'immutable';
import React, { ReactElement, ReactNode } from 'react';
import BaseButton from 'app/components/common/BaseButton';
import { useCombineCallbackRefs } from 'app/utils/react';
import { IOptionControlProps } from 'app/components/forms/SelectState/Option';

type RenderOption<T> = (id: T) => ReactNode;

export enum DropdownVariant {
  Default,
  LessonMaterial,
}

interface IDropdownProps<T extends string = string> {
  'aria-label'?: string;
  disabled?: boolean;
  onSelect: (optionId: T) => void;
  optionIds: List<T>;
  optionLabels: List<string>;
  renderOption?: RenderOption<T>;
  selectedId?: T;
  variant?: DropdownVariant;
}

const Dropdown = <T extends string = string>({
  'aria-label': ariaLabel,
  disabled = false,
  onSelect,
  optionIds,
  optionLabels,
  renderOption = (id: T) => id,
  selectedId,
  variant = DropdownVariant.Default,
}: IDropdownProps<T>) => {
  const css = useCss();

  return (
    <SelectState
      optionIds={optionIds}
      optionLabels={optionLabels}
      selectedId={selectedId}
      onSelect={onSelect}
    >{({
      isOpen,
      focusedId,
      SelectWrapper,
      Option,
    }) => {
      const applyLessonMaterialStyles = variant === DropdownVariant.LessonMaterial && !isOpen;

      const beforeSelected: ReactElement[] = [];
      const afterSelected: ReactElement[] = [];
      let selected: ReactElement;

      optionIds.forEach((optionId, index) => {
        const isFocused = optionId === focusedId;
        const isSelected = optionId === selectedId;

        const optionElement = (
          <Option key={optionId} id={optionId}>{(optionControlProps) => (
            <DropdownButton
              disabled={disabled}
              isOpen={isOpen}
              focused={isFocused}
              optionControlProps={optionControlProps}
              optionId={optionId}
              renderOption={renderOption}
              selected={isSelected}
            />
          )}</Option>
        );

        if (isSelected) {
          selected = optionElement;
        } else if (!selected) {
          beforeSelected.push(optionElement);
        } else {
          afterSelected.push(optionElement);
        }
      });

      return (
        <div
          className={css('dropdownContainer', {
            alignItems: 'stretch',
            // This will increase the contrast of the selector little bit on
            // white backgrounds while being invisible when background matches
            // PAGE_BACKGROUND_COLOR
            border: applyLessonMaterialStyles ? 'none' : `1px solid ${PAGE_BACKGROUND_COLOR}`,
            // one px more than in the wrap to match the curvature with the
            // 1px border here
            borderRadius: applyLessonMaterialStyles ? '0' : '3px',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 0,
            height: BASE_HEIGHT_PX,
            overflow: 'visible',
            position: 'relative',
            // when hiding, switch z-index after the transition for other
            // properties ended
            transition: isOpen ? undefined : `z-index 0s ${DURATION} linear`,
            width: variant === DropdownVariant.LessonMaterial ? '100%' : undefined,
            zIndex: isOpen ? 1 : 0,
          })}
        >
          <SelectWrapper>
            {elementProps => (
              <div
                aria-label={ariaLabel}
                className={css('wrap', {
                  alignItems: 'stretch',
                  background: '#fff',
                  borderRadius: '2px',
                  boxShadow: isOpen
                    ? SEARCH_INPUT_FOCUS_BOX_SHADOW
                    : variant !== DropdownVariant.LessonMaterial
                    ? SEARCH_INPUT_BOX_SHADOW
                    : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  flexShrink: isOpen ? 0 : 1,
                  overflow: 'hidden',
                  transition: `flex-shrink ${DURATION} ease-out, box-shadow ${DURATION} ease-out`,

                  ':focus': {
                    // Remove browser default focus and apply our own
                    outline: 'none',
                    boxShadow: SEARCH_INPUT_FOCUS_BOX_SHADOW,
                  },
                })}
                {...elementProps}
              >
                <div
                  className={css('beforeSelected', {
                    ...groupsBase,
                    // When opening the dropdown, this makes options above the
                    // selected ones stick to the bottom and they'll be revealed by
                    // showing the bottom ones first
                    justifyContent: 'flex-end',
                  })}
                >
                  {beforeSelected}
                </div>

                {selected}

                <div
                  className={css('afterSelected', {
                    ...groupsBase,
                    // This will make options below the selected one roll out showing
                    // top ones first
                    justifyContent: 'flex-start',
                  })}
                >
                  {afterSelected}
                </div>
              </div>
            )}
          </SelectWrapper>
        </div>
      );
    }}
    </SelectState>
  );
};

interface IDropdownButton<T extends string = string> {
  disabled: boolean;
  focused: boolean;
  isOpen: boolean;
  optionControlProps: IOptionControlProps;
  optionId: T;
  renderOption: RenderOption<T>;
  selected: boolean;
}

const DropdownButton = <T extends string = string>({
  disabled,
  isOpen,
  focused,
  optionControlProps: { ref: optionControlRef, ...optionControlProps },
  optionId,
  renderOption,
  selected,
}: IDropdownButton<T>) => {
  const css = useCss();
  const [isHovered, hoverRef] = useHoverState();
  const getRef = useCombineCallbackRefs([hoverRef, optionControlRef]);

  return (
    <BaseButton
      {...optionControlProps}
      getRef={getRef}
      className={css('option', {
        position: 'relative',
        height: BASE_HEIGHT_PX,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        textAlign: 'left',
        overflow: 'hidden',
        padding: `0 ${BASE_HEIGHT_PX} 0 20px`,
        color: 'inherit',
        backgroundColor: 'transparent',
        border: '0',
        outline: '0',
        cursor: 'pointer',
      })}
      disabled={disabled}
    >
      <span>
        {renderOption(optionId)}
      </span>

      <div className={css('cover', cover(isOpen, focused, selected && isHovered))} />

      {selected && <div
        className={css('arrow', {
          position: 'absolute',
          top: '0',
          right: '0',
          bottom: '0',
          width: BASE_HEIGHT_PX,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: 'none',
          transition: `transform ${DURATION} linear`,
          // When open, make arrow point toward option instead of pointing down
          transform: isOpen ? 'rotateZ(90deg)' : undefined,
        })}
      >
        <ActionArrow size={ICON_SIZE_SMALL_PX} />
      </div>}
    </BaseButton>
  );
};

const DURATION = '150ms';

const groupsBase: IStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  overflow: 'hidden',
};

const cover = (isOpen: boolean, isFocused: boolean, isHovered: boolean): IStyles => {
  let withArrow;
  let brightness;

  if ((!isOpen && isHovered) || (isOpen && isFocused)) {
    withArrow = false;
    brightness = 0.93;
  } else if (isOpen) {
    withArrow = false;
    brightness = 1;
  } else {
    withArrow = true;
    brightness = 1;
  }

  const c             = Math.floor(brightness * 255);
  const colorLeft     = `rgba(${c},${c},${c},0)`;
  const colorRight    = `rgba(${c},${c},${c},1)`;
  const gradientStart = `${withArrow ? BASE_HEIGHT * 0.75 : 0.25}px`;
  const gradientEnd   = `${BASE_HEIGHT}px`;
  const background    = `rgba(0,0,0,${1 - brightness}) linear-gradient(270deg,
                                        ${colorRight} ${gradientStart},
                                        ${colorLeft}  ${gradientEnd})`;

  return {
    background,
    bottom: '0',
    left: '0',
    pointerEvents: 'none',
    position: 'absolute',
    right: '0',
    top: '0',
  };
};

export default Dropdown;
